import type { Channel, Me } from '@novachat/shared';
import { Mic, MicOff, PhoneOff } from 'lucide-react';
import { firstName } from '../lib/avatar';
import { FLOW, ShownBar, useConnectionHealth } from '../state/health';
import { useLive } from '../state/live';
import { useVoice } from '../state/voice';
import { Avatar } from './Avatar';

/**
 * Under the channel's header while its call is going: a full-width coloured bar with who is in it
 * and a way in. While you are in it with the chat on screen instead of the call, it carries your
 * microphone, the way back to the call and the way out. Its colour is your connection: green,
 * yellow when it weakens, red when it is poor or cut (and then it says so).
 */
export function CallBar({
  channel,
  me,
  onShowCall,
  elsewhere: away = false,
}: {
  channel: Channel;
  me: Pick<Me, 'id'>;
  onShowCall?: () => void;
  /** Shown over another page (your call goes on in `channel`): it names the channel. */
  elsewhere?: boolean;
}) {
  const inside = useLive((s) => s.voice[channel.id]);
  const here = useVoice((s) => s.channelId === channel.id && s.room !== null);
  const connecting = useVoice((s) => s.channelId === channel.id && s.connection === 'connecting');
  const micOn = useVoice((s) => s.micOn);
  const mayJoin = channel.permissions.includes('voice_join');
  const health = useConnectionHealth();
  if (channel.kind === 'dm' || !inside?.length) return null;

  const elsewhere = !here && inside.some((p) => p.userId === me.id);
  const sharing = inside.find((p) => p.screenSharing);
  const round = 'nc-press grid size-9 flex-none place-items-center rounded-full border-0';
  const summary = (
    <>
      <span className="block truncate text-[13.5px] font-bold">
        {here ? 'Görüşmedesin' : 'Görüşme sürüyor'}
        {away ? ` · ${channel.name}` : ''} · {inside.length} kişi
      </span>
      <span className={`block truncate text-xs ${health.text ? 'font-semibold text-white' : 'text-white/85'}`}>
        {health.text ?? (sharing ? `${firstName(sharing.name)} ekranını paylaşıyor` : inside.map((p) => firstName(p.name)).join(', '))}
      </span>
    </>
  );

  return (
    <div role="region" aria-label={away ? 'Devam eden görüşme' : 'Görüşme'} className={`${FLOW[health.level]} flex min-h-12 flex-none items-center gap-3 px-3 py-1.5 text-white md:px-5`}>
      <ShownBar />
      {here && (
        <button
          type="button"
          aria-pressed={micOn}
          aria-label={micOn ? 'Mikrofonu kapat' : 'Mikrofonu aç'}
          onClick={() => void useVoice.getState().toggleMic()}
          className={`${round} ${micOn ? 'bg-white/25' : 'bg-black/25'}`}
        >
          {micOn ? <Mic size={18} /> : <MicOff size={18} />}
        </button>
      )}
      <span className="flex flex-none -space-x-1.5" aria-hidden="true">
        {inside.slice(0, 4).map((p) => (
          <Avatar key={p.userId} id={p.userId} name={p.name} size={26} />
        ))}
      </span>
      {here && onShowCall ? (
        <button type="button" onClick={onShowCall} aria-label={away ? `${channel.name} görüşmesine dön` : 'Görüşmeyi aç'} className="min-w-0 flex-1 border-0 bg-transparent p-0 text-left text-white">
          {summary}
        </button>
      ) : (
        <div className="min-w-0 flex-1">{summary}</div>
      )}
      {here ? (
        <button type="button" aria-label="Görüşmeden ayrıl" onClick={() => void useVoice.getState().leave()} className={`${round} ${LEAVE[health.level]}`}>
          <PhoneOff size={18} />
        </button>
      ) : (
        mayJoin && (
          <button
            type="button"
            disabled={connecting}
            onClick={() => void useVoice.getState().join(channel.id)}
            title={elsewhere ? 'Başka bir cihazda bu görüşmedesin; buraya alınca oradaki bağlantı kapanır.' : 'Mikrofonun kapalı katılırsın.'}
            className={`nc-press h-9 flex-none rounded-full border-0 bg-white px-4 text-[13.5px] font-bold disabled:opacity-70 ${INK[health.level]}`}
          >
            {connecting ? 'Bağlanıyor…' : elsewhere ? 'Bu cihaza al' : 'Katıl'}
          </button>
        )
      )}
    </div>
  );
}

/** The leave button stays red, white on a red bar so it does not melt into it. */
export const LEAVE = { good: 'bg-red-400 text-white', fair: 'bg-red-400 text-white', poor: 'bg-white text-red-600' } as const;
/** The white "Katıl" button's text in the bar's colour. */
const INK = { good: 'text-green-700', fair: 'text-yellow-800', poor: 'text-red-600' } as const;
